import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { X, RotateCcw, Check } from 'lucide-react';

export const SignatureCapture = ({ open, onClose, onSave, patientName }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1a1a2e';
      setHasDrawn(false);
    }
  }, [open]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col" data-testid="signature-capture">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="signature-close-btn">
          <X className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <p className="font-semibold text-slate-900 text-sm">Semnatura pacientului</p>
          <p className="text-xs text-slate-500">{patientName}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={clearCanvas} data-testid="signature-clear-btn">
          <RotateCcw className="w-5 h-5" />
        </Button>
      </div>

      <div className="flex-1 relative bg-white p-4">
        <p className="absolute top-6 left-0 right-0 text-center text-slate-300 text-sm pointer-events-none select-none">
          Semnati cu degetul in caseta de mai jos
        </p>
        <canvas
          ref={canvasRef}
          className="w-full h-full border-2 border-dashed border-slate-300 rounded-xl touch-none bg-white"
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          data-testid="signature-canvas"
        />
      </div>

      <div className="px-4 py-4 border-t bg-slate-50">
        <Button
          onClick={handleSave}
          disabled={!hasDrawn}
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold"
          data-testid="signature-save-btn"
        >
          <Check className="w-5 h-5 mr-2" />
          Salveaza semnatura
        </Button>
      </div>
    </div>
  );
};
